/**
 * TodoBoard — 나의 할일 본문(D-1 오늘 · D-2 7일 · 슬라이드 '나의 할일' 탭 공용). 이번 주는 현장 × 날짜 격자(WeekGrid).
 * GET /schedule/todos?scope=week 한 번으로 오늘 할일(items)과 7일 일정(weekDays)을 함께 받는다(useTodoSample → useTodos).
 * 조회 오류·신호 누락(SIGNAL_MISSING)은 오류 상태로 보여주고 샘플로 덮지 않는다.
 * 줄 순서 · 칸 · 제목 · 사유 · 버튼 · NEW 는 서버가 정한 그대로 그린다(할일 코드는 보이지 않음).
 * 슬라이드(embedded)는 siteId 로 현장 필터를 걸고(siteName 은 표시용), 탭은 호출부의 로컬 상태로 구동한다.
 */
import { useMemo, useState } from 'react';
import {
  Card, Checkbox, Combobox, EmptyState, ErrorState, PageHeader, Segmented, Skeleton, StatusHint, Tabs, type ComboboxOption,
} from '@indipia/ui-core';
import { useAuthStore, SignalMissingError } from '@indipia/shared';
import { useCompanyMembers } from '@indipia/shared-features';
import { SignalDot } from './SignalDot';
import { TodoRow, TodoRows } from './TodoRow';
import { WeekGrid } from './WeekGrid';
import type { TodoItem } from './todoModel';
import {
  OWNER_ALL, countByOwnerToday, countToday, countWeek, ownersOf,
  resolveOwner, useTodoOwner, visibleToday, visibleWeek,
} from './todoFilters';
import { useTodoNavigate } from './useTodoNavigate';
import { useTodoSample } from './useTodoSample';

export type TodoTab = 'today' | 'week';

export interface TodoBoardProps {
  tab: TodoTab;
  onTabChange: (tab: TodoTab) => void;
  /** 슬라이드 안에서 현장 하나로 좁힐 때 현장 ID(필터 기준) */
  siteId?: number;
  /** 슬라이드 머리의 현장명(표시용) */
  siteName?: string;
  /** 줄·버튼으로 간트로 이동하기 직전(슬라이드를 닫는 용도) */
  onBeforeNavigate?: () => void;
  /** 슬라이드 안 — 페이지 머리(PageHeader) 대신 자체 제목행 */
  embedded?: boolean;
}

const ALL_VALUE = '__all';

/** 오늘 탭 할일 보기 — 날짜별(지금 처리할 일 · 오늘 일정 칸) · 현장별(현장 카드). 이번 주 탭은 항상 격자라 전환이 없다. */
type WeekView = 'date' | 'site';
const WEEK_VIEW_OPTIONS = [
  { label: '날짜별', value: 'date' },
  { label: '현장별', value: 'site' },
];

const pad2 = (n: number) => String(n).padStart(2, '0');
/** 조회 시각 기준 표기 — 'M/D HH:MM 조회 기준'(고정 시각 문구 없음) */
function fetchedLabel(ms: number): string {
  if (!ms) return '조회 중';
  const d = new Date(ms);
  return `${d.getMonth() + 1}/${d.getDate()} ${pad2(d.getHours())}:${pad2(d.getMinutes())} 조회 기준`;
}

export function TodoBoard({ tab, onTabChange, siteId, siteName, onBeforeNavigate, embedded = false }: TodoBoardProps) {
  const { todos, week, isLoading, isError, error, fetchedAt, refetch } = useTodoSample({ scope: 'week', siteId });
  const [storedOwner, setOwner] = useTodoOwner();
  const myId = useAuthStore((s) => s.user?.memberId);
  const { data: members } = useCompanyMembers();
  const goto = useTodoNavigate(onBeforeNavigate);

  const memberNames = useMemo(() => new Map((members ?? []).map((m) => [m.memberId, m.memberName] as const)), [members]);
  const owner = resolveOwner(storedOwner, todos);
  const ownerIds = useMemo(() => ownersOf(todos, memberNames), [todos, memberNames]);
  const ownerLabel = (o: string) => {
    if (o === OWNER_ALL) return '전체';
    const id = Number(o);
    const name = memberNames.get(id) ?? `담당자 ${o}`;
    return id === myId ? `${name} (나)` : name;
  };

  const filter = { owner, siteId };
  const todayN = countToday(todos, filter);
  const [weekView, setWeekView] = useState<WeekView>('date');
  const [actionOnly, setActionOnly] = useState(false);
  const weekDays = useMemo(() => visibleWeek(week, { owner, siteId, actionOnly }), [week, owner, siteId, actionOnly]);
  const weekN = countWeek(week, filter);
  const ownerCounts = countByOwnerToday(todos, siteId);

  const ownerOptions: ComboboxOption[] = [OWNER_ALL, ...ownerIds.map(String)].map((o) => ({
    value: o === OWNER_ALL ? ALL_VALUE : o,
    label: `담당자: ${ownerLabel(o)}`,
  }));

  const weekRange = week.length > 0 ? `${week[0].label} ~ ${week[week.length - 1].label}` : fetchedLabel(fetchedAt);
  const base = (tab === 'today' ? fetchedLabel(fetchedAt) : weekRange) + (siteName ? ` · 현장 ${siteName}` : '');

  const controls = (
    <div className="flex flex-wrap items-center gap-3">
      {/* 보기 전환(날짜별 · 현장별)은 오늘만 — 이번 주는 격자 하나. [조치 필요만](지금 처리할 일만)은 공통, 탭을 오가도 유지 */}
      {tab === 'today' && (
        <Segmented size="sm" aria-label="할일 보기" options={WEEK_VIEW_OPTIONS} value={weekView}
          onChange={(v) => setWeekView(v as WeekView)} />
      )}
      {/* 담당자 필터는 오늘만 — 7일 일정 줄(입고·착공·공사완료)에는 담당자가 없다 */}
      {tab === 'today' && (
        <div className="w-56">
          <Combobox
            size="sm"
            aria-label="담당자 필터"
            value={owner === OWNER_ALL ? ALL_VALUE : owner}
            options={ownerOptions}
            onChange={(v) => setOwner(v === ALL_VALUE ? OWNER_ALL : v)}
            renderOption={(opt) => {
              const o = opt.value === ALL_VALUE ? OWNER_ALL : opt.value;
              return (
                <span className="flex w-full items-center justify-between gap-3">
                  <span>
                    {ownerLabel(o)}
                    {o === OWNER_ALL && <span className="ml-1.5 text-caption text-text-muted">내가 관리자인 현장</span>}
                  </span>
                  <span className="text-caption font-bold text-text-muted">{ownerCounts[o] ?? 0}</span>
                </span>
              );
            }}
          />
        </div>
      )}
      {/* 두 탭 공통 [조치 필요만]은 탭 바로 앞 — 오늘 전용 조건이 왼쪽에 붙고 빠져도 체크박스·탭 자리는 그대로 */}
      <Checkbox label="조치 필요만" checked={actionOnly} onChange={(e) => setActionOnly(e.target.checked)} />
      {/* 탭 높이를 왼쪽 조건(sm, 32px)과 맞춤 */}
      <Tabs
        variant="pill"
        size="sm"
        tabs={[
          { label: isError ? '오늘' : `오늘 ${todayN}`, value: 'today' },
          { label: isError ? '이번 주' : `이번 주 ${weekN}`, value: 'week' },
        ]}
        value={tab}
        onChange={(v) => onTabChange(v as TodoTab)}
      />
    </div>
  );

  const emptyPrefix = siteName ? '이 현장의 ' : '';
  // 상태 블록(D-07 §17.1) — 로딩은 Skeleton, 오류는 ErrorState(다시 시도), 0건은 EmptyState. 오늘 · 이번 주 공용.
  const loadingBlock = (
    <div className="flex flex-col gap-4" aria-busy="true">
      <Skeleton className="h-32 rounded-xl" />
      <Skeleton className="h-32 rounded-xl" />
    </div>
  );
  // 오류·신호 누락은 장애 — 샘플·로컬 값으로 대체하지 않고 명시적으로 알린다.
  // 오류·빈 상태는 본문 영역(머리 아래 남은 높이) 세로·가로 중앙 — 루트가 flex 열이라 flex-1 이 남은 높이를 채운다.
  const centered = (node: React.ReactNode) => <div className="flex flex-1 items-center justify-center">{node}</div>;
  const errorBlock = centered(
    <ErrorState
      description={error instanceof SignalMissingError ? error.message : '할일을 불러오지 못했어요. 잠시 후 다시 시도해주세요.'}
      onRetry={() => void refetch()}
    />,
  );
  const emptyBlock = (title: string) => centered(<EmptyState title={title} />);

  let content: React.ReactNode;
  if (tab === 'today') {
    if (isLoading) {
      content = loadingBlock;
    } else if (isError) {
      content = errorBlock;
    } else {
      const vis = visibleToday(todos, filter);
      const red = vis.red;
      // [조치 필요만]은 오늘 일정(노랑) 칸을 숨긴다
      const yellow = actionOnly ? [] : vis.yellow;
      const section = (color: 'red' | 'yellow', title: string, sub: string, list: TodoItem[], emptyText: string) => (
        <Card className="[&>.body]:p-0">
          <div className="flex items-center gap-2 border-b border-border px-5 py-3">
            <SignalDot color={color} />
            <h3 className="text-heading-sm font-bold text-text">{title}</h3>
            <span className="text-caption text-text-muted">{sub}</span>
          </div>
          {list.length === 0 ? (
            <div className="flex justify-center px-5 py-7"><StatusHint>{emptyText}</StatusHint></div>
          ) : (
            <TodoRows items={list} showOwner={owner === OWNER_ALL} onNavigate={goto} />
          )}
        </Card>
      );
      if (weekView === 'site') {
        // 현장별 — 현장 카드마다 그 현장의 지금 처리할 일 → 오늘 일정(서버 순서), 앞칸 = 칸 이름(바뀌는 첫 줄에만)
        const groups = new Map<number, { site: string; owner: string; items: TodoItem[] }>();
        for (const t of [...red, ...yellow]) {
          const g = groups.get(t.siteId) ?? { site: t.site, owner: t.owner, items: [] };
          g.items.push(t);
          groups.set(t.siteId, g);
        }
        content = groups.size === 0 ? (
          emptyBlock(`${emptyPrefix}${actionOnly ? '지금 처리할 일이 없어요' : '오늘 챙길 일이 없어요'}`)
        ) : (
          <div className="flex flex-col gap-4">
            {[...groups.entries()].map(([sid, g]) => (
              <Card key={sid} className="[&>.body]:p-0">
                <div className="flex items-center gap-2 border-b border-border px-5 py-3">
                  <h3 className="truncate text-heading-sm font-bold text-text" title={g.site}>{g.site}</h3>
                  {owner === OWNER_ALL && g.owner && <span className="text-caption text-text-muted">담당 {g.owner}</span>}
                  <span className="text-caption text-text-muted">{g.items.length}건</span>
                </div>
                {g.items.map((t, i) => (
                  <TodoRow
                    key={t.id}
                    todo={t}
                    showSite={i === 0 || g.items[i - 1].level !== t.level}
                    lead={t.level === 'r' ? '지금 처리' : '오늘 일정'}
                    onNavigate={goto}
                  />
                ))}
              </Card>
            ))}
          </div>
        );
      } else {
        content = (
          <div className="flex flex-col gap-4">
            {section('red', '지금 처리할 일', '빨강 · 현장 순', red, `${emptyPrefix}지금 처리할 일이 없어요`)}
            {!actionOnly && section('yellow', '오늘 일정', '노랑 · 현장 순', yellow, `${emptyPrefix}오늘 일정이 없어요`)}
          </div>
        );
      }
    }
  } else if (isLoading) {
    content = loadingBlock;
  } else if (isError) {
    content = errorBlock;
  } else if (weekDays.length === 0) {
    content = emptyBlock(`${emptyPrefix}${actionOnly ? '이번 주에 조치가 필요한 일이 없어요' : '이번 주에 챙길 일이 없어요'}`);
  } else {
    // 이번 주 — 행 = 현장, 열 = 날짜. 오늘 열 = 오늘 할일 + 일정, 그 뒤 날짜 = 입고 · 착공 · 공사완료 일정
    content = <WeekGrid allDays={week} days={weekDays} showOwner={owner === OWNER_ALL} onOpen={(t) => goto(t, 'row')} />;
  }

  return (
    <div className={embedded ? 'flex flex-1 flex-col gap-4 p-5' : 'flex flex-1 flex-col gap-4'}>
      {embedded ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-heading-sm font-bold text-text">나의 할일</h2>
            <span className="text-caption text-text-muted">{base}</span>
          </div>
          {controls}
        </div>
      ) : (
        <PageHeader title="나의 할일" subtitle={base} extra={controls} />
      )}
      {content}
    </div>
  );
}
